import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  read: boolean;
  created_at: string;
}

interface Conversation {
  id: string;
  participant1_id: string;
  participant2_id: string;
  listing_id: string | null;
  created_at: string;
  updated_at: string;
}

interface Profile {
  id: string;
  full_name: string | null;
  email: string;
}

const formatMessageDate = (date: string) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (messageDate.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return messageDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }
};

export default function ConversationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [otherUser, setOtherUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchConversation();
    fetchMessages();
  }, [id, user]);

  const fetchConversation = async () => {
    try {
      const { data: convo, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // Check if user is part of this conversation
      if (convo.participant1_id !== user?.id && convo.participant2_id !== user?.id) {
        navigate('/messages');
        return;
      }

      setConversation(convo);

      // Get other user's profile
      const otherUserId = convo.participant1_id === user?.id 
        ? convo.participant2_id 
        : convo.participant1_id;

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('id', otherUserId)
        .single();

      setOtherUser(profile);
    } catch (error) {
      navigate('/messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setMessages(data || []);

      // Mark messages as read
      if (data && data.length > 0 && user?.id) {
        await supabase
          .from('messages')
          .update({ read: true })
          .eq('conversation_id', id)
          .neq('sender_id', user.id)
          .eq('read', false);
      }
    } catch (error) {
      // silently handle
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);

      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: id,
          sender_id: user?.id,
          content: newMessage.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Add message to UI
      setMessages([...messages, data]);
      setNewMessage('');

      // Update conversation timestamp
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', id);

    } catch (error) {
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {/* Chat Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 mt-24">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/messages')}
            className="text-gray-600 hover:text-gray-900 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <i className="ri-user-line text-blue-600"></i>
          </div>
          
          <div>
            <h2 className="font-semibold text-gray-900">
              {otherUser?.full_name || otherUser?.email || 'User'}
            </h2>
            <p className="text-sm text-gray-500">Active</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-chat-3-line text-4xl text-gray-300 mb-2"></i>
              <p className="text-gray-600">No messages yet</p>
              <p className="text-sm text-gray-500 mt-1">Start the conversation!</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const isOwn = message.sender_id === user?.id;
              const showDateSeparator = index === 0 || 
                new Date(messages[index - 1].created_at).toDateString() !== 
                new Date(message.created_at).toDateString();
              
              return (
                <div key={message.id}>
                  {/* Date Separator */}
                  {showDateSeparator && (
                    <div className="flex justify-center my-4">
                      <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                        {formatMessageDate(message.created_at)}
                      </span>
                    </div>
                  )}
                  
                  {/* Message Bubble */}
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
                    <div
                      className={`max-w-md px-4 py-2 rounded-2xl shadow-sm ${
                        isOwn
                          ? 'bg-blue-600 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content.split(/(\s+)/).map((token, i) => {
                          if (token.startsWith('https://')) {
                            return (
                              <a
                                key={i}
                                href={token}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`underline font-semibold break-all ${isOwn ? 'text-blue-200' : 'text-blue-600'}`}
                              >
                                Tap to complete payment →
                              </a>
                            );
                          }
                          return token;
                        })}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isOwn ? 'text-blue-100' : 'text-gray-500'
                        }`}
                      >
                        {new Date(message.created_at).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={sendMessage} className="flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-md cursor-pointer"
            >
              {sending ? (
                <i className="ri-loader-4-line animate-spin text-lg"></i>
              ) : (
                <i className="ri-send-plane-fill text-lg"></i>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
